/**
 * gapImpact — UI-facing facade over RV-A's canonical
 * `effectiveGapImpact` in `providerImpactForIntent.ts`.
 *
 * RV-A landed the full intent-vs-provider matrix and exposed
 * `effectiveGapImpact(gap, intent) -> ProviderImpactAssessment`
 * with severity union `'critical' | 'significant' | 'minor' |
 * 'irrelevant'`. RV-B's customer-facing "What this report does not
 * know" appendix prefers the softer label `'noted'` over `'minor'`,
 * and only needs the severity (not the reason string).
 *
 * This module delegates the matrix lookup to RV-A and renames
 * `'minor'` -> `'noted'` at the UI boundary. Keep this as the only
 * place that performs the rename so the canonical vocabulary
 * elsewhere stays internally consistent.
 */
import type { DataGap } from '../types/siteEvidence';
import type { ProjectIntent } from '../types/siteRag';
import { effectiveGapImpact as canonicalEffectiveGapImpact } from './providerImpactForIntent';

/** Effective impact tier — what the appendix and the SectionReadiness
 *  panel render. Mirrors RV-A's `ImpactSeverity` with `'minor'`
 *  renamed to `'noted'` for customer-facing copy. */
export type EffectiveGapImpact = 'critical' | 'significant' | 'noted' | 'irrelevant';

/**
 * Return the effective impact of a gap for a given project intent.
 * Delegates to RV-A's canonical helper and renames the internal
 * `'minor'` tier to the customer-facing `'noted'`.
 */
export function effectiveGapImpact(
  gap: DataGap,
  intent: ProjectIntent | undefined | null,
): EffectiveGapImpact {
  const { severity } = canonicalEffectiveGapImpact(gap, intent);
  return severity === 'minor' ? 'noted' : severity;
}

/**
 * True when a gap should surface in the customer-facing "What this
 * report does not know" appendix. Anything not classified as
 * `'irrelevant'` by the matrix is worth showing.
 */
export function gapShouldSurface(impact: EffectiveGapImpact): boolean {
  return impact !== 'irrelevant';
}
